import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const ContentSection = styled.section`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  text-align: center;
  margin-bottom: 40px;
`;

const InfoText = styled.p`
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.neutral.grey};
  text-align: center;
  margin-bottom: 40px;
`;

const OptionsSection = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px;
  margin-bottom: 32px;
`;

const OptionTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const OptionCard = styled.div`
  border: 2px solid ${theme.colors.neutral.lightGrey};
  border-radius: ${theme.borderRadius.lg};
  padding: 24px;
  margin-bottom: 24px;
  background: #f9f9f9;
`;

const OptionLabel = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.bold};
  color: #232B3B;
  margin-bottom: ${theme.spacing.sm};
`;

const OptionDescription = styled.p`
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const PhoneNumber = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: #232B3B;
  text-decoration: none;
  margin-bottom: ${theme.spacing.sm};
  
  &:hover {
    color: #FFD700;
  }
`;

const SmallText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.neutral.grey};
  font-style: italic;
`;

const EmailInfo = styled.div`
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.text.primary};
  
  strong {
    color: ${theme.colors.text.primary};
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.neutral.lightGrey};
  margin: 40px 0;
`;

const ActionSection = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 32px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const QuestionBox = styled.div`
  background-color: #f9f9f9;
  border: 2px solid #FFD700;
  border-radius: ${theme.borderRadius.md};
  padding: 20px;
  position: relative;
  margin-bottom: ${theme.spacing.md};
`;

const QuestionText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text.primary};
  font-style: italic;
  line-height: 1.6;
  padding-right: 60px;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 10px 16px;
  background-color: #FFD700;
  color: #232B3B;
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  font-weight: ${theme.typography.fontWeights.semibold};
  font-size: ${theme.typography.sizes.sm};
  
  &:hover {
    background-color: #e6c200;
  }
`;

const VideoContainer = styled.div`
  background-color: #f9f9f9;
  border-radius: ${theme.borderRadius.lg};
  padding: 20px;
  text-align: center;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.neutral.grey};
  font-style: italic;
`;

const ContactSection = styled.div`
  text-align: center;
  padding: 32px;
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ContactText = styled.p`
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.neutral.grey};
  font-style: italic;
`;

const ScheduleButton = styled.button`
  display: inline-block;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #232B3B;
  padding: 16px 32px;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 16px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  }
`;

const ThankYou: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { openCalendly } = useCalendlyPopup();
  const [showCopied, setShowCopied] = useState(false);
  const [userEmail, setUserEmail] = useState('your registered email');

  useEffect(() => {
    // Check if user came from form submission
    if (!location.state?.fromForm) {
      // If not from form, redirect to home
      navigate('/');
    }
    
    // Get user email from state if available
    if (location.state?.email) {
      setUserEmail(location.state.email);
    }
  }, [location, navigate]);

  const quintessentialQuestion = `"Could you backtest the 10-year historical performance of the [insert annuity name] structured annuity product, specifically analyzing how the managed volatility component affected real returns versus the underlying index (S&P 500), and show me how the date lottery (monthly point-to-point crediting) would have impacted investor outcomes based on different start dates?"`;

  const handleCopyQuestion = () => {
    navigator.clipboard.writeText(quintessentialQuestion);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  return (
    <SharedLayout>
      <PageWrapper>
        <ContentSection>
          <PageTitle>We're Analyzing Your Request</PageTitle>
          <Subtitle>Your backtest request has been received.</Subtitle>
          <InfoText>
            Our AI tool typically processes results instantly, but we're currently 
            experiencing high demand from the Financial Freedom show.
          </InfoText>

          <OptionsSection>
            <OptionTitle>What Happens Next:</OptionTitle>
            
            <OptionCard>
              <OptionLabel>Option 1: Get Results by Phone (Fastest)</OptionLabel>
              <OptionDescription>
                Our team can run your analysis immediately and explain the results.
              </OptionDescription>
              <PhoneNumber href="tel:800-922-1894">
                📞 Call Now: 800-922-1894
              </PhoneNumber>
              <SmallText>Average call time: 15 minutes</SmallText>
            </OptionCard>

            <OptionCard>
              <OptionLabel>Option 2: Schedule a Consultation</OptionLabel>
              <OptionDescription>
                Want to discuss your results at a time that works for you? Schedule a free consultation with our experts.
              </OptionDescription>
              <ScheduleButton onClick={openCalendly}>
                📅 Schedule Your Free Consultation
              </ScheduleButton>
              <SmallText>Choose a time that works best for you</SmallText>
            </OptionCard>
          </OptionsSection>

          <Divider />

          <SectionTitle style={{ marginBottom: '32px', fontSize: theme.typography.sizes.xl }}>
            While You Wait:
          </SectionTitle>

          <ActionSection>
            <SectionTitle>Copy our Quintessential Question</SectionTitle>
            <p style={{ marginBottom: '16px', color: theme.colors.neutral.grey }}>
              and ask ChatGPT about your annuity:
            </p>
            <QuestionBox>
              <QuestionText>{quintessentialQuestion}</QuestionText>
              <CopyButton onClick={handleCopyQuestion}>
                {showCopied ? 'Copied!' : 'Copy'}
              </CopyButton>
            </QuestionBox>
          </ActionSection>

          <Divider />

          <ContactSection>
            <ContactText>Questions? Call us at 800-922-1894</ContactText>
          </ContactSection>
        </ContentSection>
      </PageWrapper>
    </SharedLayout>
  );
};

export default ThankYou;